import React, { useState, useEffect, useRef } from "react";
import fetch from "isomorphic-fetch";
import Client from "shopify-buy";

// This is where we interact with the Shopify Buy SDK

export const ShopifyContext = React.createContext();

const client = Client.buildClient(
  {
    storefrontAccessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
    domain: `checkout.thermacell.com`,
  },
  fetch
);

const ShopifyProvider = ({ children }) => {
  const initialStoreState = {
    client,
    adding: false,
    checkout: { lineItems: [] },
    products: [],
    shop: {},
  };

  const [store, updateStore] = useState(initialStoreState);
  const [variantImage, setVariantImage] = useState(null);
  const isRemoved = useRef(false);

  useEffect(() => {
    const createNewCheckout = () => store.client.checkout.create();
    const fetchCheckout = (id) => store.client.checkout.fetch(id);
    const initializeCheckout = async () => {
      // Check for an existing cart.
      const isBrowser = typeof window !== "undefined";
      const existingCheckoutID = isBrowser
        ? localStorage.getItem("shopify_checkout_id")
        : null;

      const setCheckoutInState = (checkout) => {
        if (isBrowser) {
          localStorage.setItem("shopify_checkout_id", checkout.id);
        }

        updateStore((prevState) => {
          return { ...prevState, checkout };
        });
      };

      if (existingCheckoutID) {
        try {
          const checkout = await fetchCheckout(existingCheckoutID);
          if (checkout.email) {
            localStorage.setItem("shopify_email", checkout.email);
          }
          if (checkout.orderStatusUrl) {
            localStorage.setItem(
              "shopify_id",
              checkout.orderStatusUrl.split("/")[3]
            );
          }
          // Make sure this cart hasn’t already been purchased.
          if (!checkout.completedAt) {
            setCheckoutInState(checkout);
            return;
          } else {
            const newCheckout = await createNewCheckout();
            setCheckoutInState(newCheckout);
          }
        } catch (e) {
          localStorage.setItem("shopify_checkout_id", null);
        }
      }
      if (
        (!isRemoved.current && !existingCheckoutID) ||
        (!isRemoved.current && existingCheckoutID === "null")
      ) {
        // Only happens on initial render and if there is no local storage checkout id
        const newCheckout = await createNewCheckout();
        setCheckoutInState(newCheckout);
      }
    };

    initializeCheckout();
  }, [store.client.checkout]);

  useEffect(() => () => {
    isRemoved.current = true;
  });

  const processAddToCartData = (variantId, quantity, recharge) => {
    if (variantId === "" || !quantity) {
      return;
    }

    updateStore((prevState) => {
      return { ...prevState, adding: true };
    });

    const { checkout, client } = store;

    const checkoutId = checkout.id;

    const {
      sellingPlanId,
      subscriptionFrequency,
      hasSubscription,
      usingSubscription,
    } = recharge || {
      sellingPlanId,
      subscriptionFrequency,
      hasSubscription,
      usingSubscription,
    };

    // Add ReCharge to payload, if hasSubscription is true
    const customAttributes = usingSubscription
      ? [
          // selling_plan is required for ReCharge's API
          {
            key: "selling_plan",
            value: sellingPlanId.toString(),
          },
          // subscription_frequency is used to render the frequency in the cart's lineitems
          {
            key: "subscription_frequency",
            value: subscriptionFrequency.toString(),
          },
        ]
      : [];

    const lineItemsToUpdate = [
      {
        variantId: btoa(variantId),
        quantity: parseInt(quantity, 10),
        customAttributes,
      },
    ];

    return {
      client,
      checkoutId,
      lineItemsToUpdate,
    };
  };

  const handleAddToCart = (client, checkoutId, lineItemsToUpdate, callback) => {
    client.checkout
      .addLineItems(checkoutId, lineItemsToUpdate)
      .then((checkout) => {
        updateStore((prevState) => {
          return { ...prevState, checkout, adding: false };
        });
      })
      .then(() => callback && callback());
  };

  return (
    <ShopifyContext.Provider
      value={{
        store,
        variantImage,
        addVariantToCart: async (variantId, quantity, recharge, callback) => {
          const cartData = processAddToCartData(variantId, quantity, recharge);
          const { client, checkoutId, lineItemsToUpdate } = cartData;
          handleAddToCart(client, checkoutId, lineItemsToUpdate, callback);
        },

        addBundleToCart: async (
          variantId,
          quantity,
          recharge,
          bundleItems,
          mountItems,
          callback
        ) => {
          const cartData = processAddToCartData(variantId, quantity, recharge);

          if (bundleItems.length) {
            bundleItems.forEach((bundleItem) => {
              const item = {
                variantId: btoa(bundleItem.id),
                quantity: parseInt(bundleItem.qty, 10),
              };
              cartData.lineItemsToUpdate.push(item);
            });
          }
          if (mountItems.length) {
            mountItems.forEach((mountItem) => {
              if (mountItem.qty > 0) {
                const item = {
                  variantId: btoa(mountItem.id),
                  quantity: parseInt(mountItem.qty, 10),
                };
                cartData.lineItemsToUpdate.push(item);
              }
            });
          }

          const { client, checkoutId, lineItemsToUpdate } = cartData;
          handleAddToCart(client, checkoutId, lineItemsToUpdate, callback);
        },

        //
        removeLineItem: (client, checkoutID, lineItemID) => {
          return client.checkout
            .removeLineItems(checkoutID, [lineItemID])
            .then((res) => {
              updateStore((prevState) => {
                return { ...prevState, checkout: res };
              });
            });
        },

        //
        updateLineItem: (client, checkoutID, lineItemID, quantity) => {
          const lineItemsToUpdate = [
            { id: lineItemID, quantity: parseInt(quantity, 10) },
          ];

          return client.checkout
            .updateLineItems(checkoutID, lineItemsToUpdate)
            .then((res) => {
              updateStore((prevState) => {
                return { ...prevState, checkout: res };
              });
            });
        },
      }}
    >
      {children}
    </ShopifyContext.Provider>
  );
};
export default ShopifyProvider;
